import { useState } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Modal } from "./Modal";
import { ActionButton, FormField, inputClass } from "./Primitives";
import { KeyRound } from "lucide-react";

export function ChangePasswordModal({ forced = false, onClose }) {
  const { user, setUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated");
      if (user) setUser({ ...user, must_change_password: false });
      onClose?.();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to change password"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={forced ? "Password change required" : "Change password"}
      onClose={forced ? undefined : onClose}
      testId="change-password-modal"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
        {forced && (
          <div className="border border-[#FF9F0A]/50 bg-[#FF9F0A]/10 text-[#FF9F0A] px-4 py-3 text-xs font-mono uppercase tracking-widest flex items-center gap-2">
            <KeyRound className="size-4" />
            An admin has required you to change your password before continuing.
          </div>
        )}
        <FormField label="Current password">
          <input
            data-testid="cpw-current"
            type="password"
            autoComplete="current-password"
            required
            className={inputClass}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </FormField>
        <FormField label="New password (min 8 chars)">
          <input
            data-testid="cpw-next"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            className={inputClass}
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </FormField>
        <FormField label="Confirm new password">
          <input
            data-testid="cpw-confirm"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            className={inputClass}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </FormField>
        <div className="flex justify-end gap-2 pt-2">
          {!forced && (
            <button
              type="button"
              onClick={onClose}
              className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white px-3 py-2"
            >
              Cancel
            </button>
          )}
          <ActionButton type="submit" data-testid="cpw-submit" disabled={submitting}>
            {submitting ? "Updating…" : "Update password"}
          </ActionButton>
        </div>
      </form>
    </Modal>
  );
}
